<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template><div class="k-asset-review" :class="`${total_score_color} lighten-5 k-asset-review-full-${full_review_showing}`">
	<div v-if="lreview">
		<div class="k-asset-review-header d-flex align-center">
			<v-icon :color="`${total_score_color} darken-3`" class="mr-1" style="margin-top:-3px;">fas fa-star-sharp</v-icon>
			<div><b>HQIM Review</b></div>
			<v-spacer/>
			<div class="ml-3">Total score:</div>
			<div class="mx-2" style="width:100px">
				<v-progress-linear
					v-model="total_score_pct"
					height="20"
					:color="`${total_score_color} darken-2`"
					rounded
				/>
			</div>
			<div :class="`${total_score_color}--text text--darken-4`"><b>{{total_score_display}} / {{lreview.max_total_score}}</b></div>
			<v-spacer/>
			<v-btn v-show="!full_review_showing" small dark class="k-tight-btn" :color="`${total_score_color} darken-3`" @click="full_review_showing = true">Show Full Review<v-icon small class="ml-2">fas fa-down-from-line</v-icon></v-btn>
			<v-btn v-show="full_review_showing" small dark class="k-tight-btn" :color="`${total_score_color} darken-3`" @click="full_review_showing = false">Hide Full Review<v-icon small class="ml-2">fas fa-up-to-line</v-icon></v-btn>
		</div>

		<v-expand-transition>
			<div v-show="full_review_showing">
				<div class="d-flex justify-center align-center my-2">
					<b>HQIM Rubric:</b>
					<div style="width:240px" class="ml-2">
						<v-select v-model="selected_template" :items="templates" :background-color="'#fff'" append-icon="fas fa-caret-down" dense outlined hide-details/>
					</div>
				</div>

				<div
					v-for="(section) in template" 
					v-if="section.asset_types.includes(asset_type)" 
					class="k-asset-review-section"
				>
					<div class="k-asset-review-section-title d-flex align-center">
						<div
							class="k-asset-review-section-score mr-2" 
							:class="scale_score_class(section, lreview.sections[section.id].score)"
						>
							{{scale_value_to_show(section)}}
						</div>
						<b v-html="section.title"></b>
					</div>

					<div v-for="(scale_description, scale_value, scale_index) in section.scale" class="k-asset-review-section-scale">
						<div class="k-asset-review-section-scale-header d-flex my-2 ml-4">
							<v-icon
								style="margin-top: -3px;"
								:class="scale_score_class(section, scale_value)"
								@click="scale_value_clicked(section, scale_value)"
							>
								{{scale_icon(section, scale_value)}}
							</v-icon>

							<b
								class="ml-2 k-asset-review-section-scale-header-value"
								:class="scale_score_class(section, scale_value)"
							>
								{{scale_value}}: 
							</b>
							<div class="ml-1" v-html="template_string(scale_description)"></div>
						</div>
					</div>

					<div class="d-flex justify-center">
						<div v-if="section.criteria">
							<v-btn v-if="!criteria_showing[section.id]" x-small color="#444" dark @click="criteria_showing[section.id] = true">Show Criteria<v-icon small class="ml-2">fas fa-list-check</v-icon></v-btn>
							<v-btn v-if="criteria_showing[section.id]" x-small color="#444" dark @click="criteria_showing[section.id] = false">Hide Criteria<v-icon small class="ml-2">fas fa-list-check</v-icon></v-btn>
						</div>
						<div class="mx-2" v-if="show_review_comments">
							<v-btn v-if="!comments_showing[section.id]" x-small color="#444" dark @click="comments_showing[section.id] = true">Show Comments<v-icon small class="ml-2">fas fa-comment</v-icon></v-btn>
							<v-btn v-if="comments_showing[section.id]" x-small color="#444" dark @click="comments_showing[section.id] = false">Hide Comments<v-icon small class="ml-2">fas fa-comment</v-icon></v-btn>
						</div>
					</div>

					<div v-if="section.criteria">
						<v-expand-transition>
							<div v-show="criteria_showing[section.id]" class="k-asset-review-section-extra ml-11">
								<div v-for="(c) in section.criteria" class="d-flex mt-1">
									<v-checkbox v-model="lreview.sections[section.id].criteria[c.id]" class="shrink mt-0 pt-0 d-inline-block" off-icon="far fa-square" on-icon="fas fa-check-square" color="#444" hide-details/>
									<div v-html="c.text" style="margin-top:2px"></div>
								</div>
							</div>
						</v-expand-transition>
					</div>

					<div v-if="show_review_comments">
						<v-expand-transition>
							<div v-show="comments_showing[section.id]" class="k-asset-review-section-extra ml-11">
								<!-- existing comments -->
								<div v-if="lreview.sections[section.id].comments.length">
									<AssetReviewComment
										v-for="(c, index) in lreview.sections[section.id].comments"
										v-if="c.parent_comment_id == ''"
										:key="section.id + '_' + index"
										class="mb-2"
										:comment="c" 
										:template="template" 
										:review_section="lreview.sections[section.id]"
										@save_comment="save_comment(section, $event)"
										@delete_comment="delete_comment(section, $event)"
									/>
								</div>
								
								<!-- no comments and no comment rights -->
								<div v-else-if="!lreview.sections[section.id].comments.length && !user_can_comment">
									<b>No Comments</b>
								</div>

								<!-- new comment creator -->
								<div v-if="!new_comment[section.id] && user_can_comment">
									<v-btn x-small color="#444" dark @click="generate_new_comment(section.id)">
										New Comment<v-icon small class="ml-2">fas fa-comment</v-icon>
									</v-btn>
								</div>

								<AssetReviewCommentEditor
									v-if="new_comment[section.id]"
									:comment="new_comment[section.id]"
									@save_comment="save_comment(section, $event)"
									@cancel_comment="new_comment[section.id] = false"
								/>
							</div>
						</v-expand-transition>
					</div>
				</div>
			</div>
		</v-expand-transition>
	</div>
</div></template>

<script>
import { mapState } from 'vuex'
import AssetReviewComment from './AssetReviewComment.vue'
import AssetReviewCommentEditor from './AssetReviewCommentEditor.vue'

export default {
	components: {AssetReviewComment, AssetReviewCommentEditor},

	props: {
		asset_review_data: { required: true },
	},

	data() { return {
		full_review_showing: false,
		criteria_showing: {},
		comments_showing: {},
		new_comment: {},
		lreview: null,
		saved_review_json: '',
		templates: [
			{ value:'default', text:'Default Rubric' },
			{ value:'ela', text:'ELA Rubric' },
			{ value:'math', text:'Math Rubric' },
			{ value:'science', text:'Science Rubric' },
			{ value:'socstud', text:'Social Studies Rubric' },
		],
		selected_template: 'default',
		// TODO: have a way to specify different templates for different subject areas
		template: [
			{
				id: 'standards_alignment',
				title: 'Standards Alignment',
				asset_types: ['lesson', 'resource', 'activity'],
				scale: {
					0: '<b>No standards</b> specified',
					1: 'Primary standard(s) specified, but content of lesson/activity <b>does not target the full depth</b> of the primary standard(s)',
					2: '$asset_type <b>targets the full depth</b> of the primary standard(s)',
				},
			},{
				id: 'student_engagement',
				title: 'Student Interactivity/Engagement',
				asset_types: ['lesson', 'resource', 'activity'],
				scale: {
					0: '$asset_type includes <b>passive</b> student content only',
					1: '$asset_type includes <b>minimal student interactivity</b>',
					2: '$asset_type includes <b>engaging</b> student interactivity, <b>appropriate</b> for learning about the standard',
					3: '$asset_type includes <b>exceptionally engaging and appropriate</b> student interactivity',
				},
				criteria: [
					{
						id: 'student_engagement_a',
						text: 'Includes student <b>collaboration</b> component',
						control: 'checkbox'
					},
					{
						id: 'student_engagement_b',
						text: 'Includes <b>online</b> interaction with <b>teacher-accessible student results</b>',
						control: 'checkbox'
					},
				]
			},{
				id: 'assessment',
				title: 'Assessment',
				asset_types: ['lesson', 'resource', 'activity'],
				scale: {
					0: '<b>No assessment guidance</b> provided',
					1: '<b>Minimal</b> assessment guidance provided',
					2: '<b>Adequate</b> assessment guidance provided',
				},
				criteria: [
					{
						id: 'assessment_a',
						text: 'Includes <b>multiple-choice</b> questions',
						control: 'checkbox'
					},
					{
						id: 'assessment_b',
						text: 'Includes <b>free response</b> questions',
						control: 'checkbox'
					},
					{
						id: 'assessment_c',
						text: 'Includes <b>other question/interaction types</b>',
						control: 'checkbox'
					},
				],
			},{
				id: 'teaching_strategies',
				title: 'Teaching Strategies',
				asset_types: ['lesson'],
				scale: {
					0: '<b>No teaching strategies</b> provided',
					1: '<b>Some teaching strategies</b> specified, but not appropriately spelled out',
					2: 'Teaching strategies that are specified are appropriately spelled out, but <b>additional elements are needed</b>',
					3: '<b>Effective teaching strategies</b> specified and appropriately spelled out',
				},
				criteria: [
					{
						id: 'teaching_strategies_a',
						text: 'Questions: Gives students the opportunity to construct and/or provides engaging questions that spark and sustain inquiry.',
						control: 'checkbox'
					},
					{
						id: 'teaching_strategies_b',
						text: 'Collaboration: Structures opportunities for students to work together using disciplinary content to develop, examine, and/or communicate ideas.',
						control: 'checkbox'
					},
					{
						id: 'teaching_strategies_c',
						text: 'Academic vocabulary: Supports development of general academic and/or content area specific vocabulary in context.',
						control: 'checkbox'
					},
					{
						id: 'teaching_strategies_d',
						text: 'Analysis: Requires critical reading of primary and/or secondary resources.',
						control: 'checkbox'
					},
					{
						id: 'teaching_strategies_e',
						text: 'Speaking: Facilitates rich and rigorous evidence-based discussions.',
						control: 'checkbox'
					},
					{
						id: 'teaching_strategies_f',
						text: 'Writing: Expects students to reference evidence to inform, explain, evaluate, or make an argument (note taking, summaries, short responses, formal essays, etc.).',
						control: 'checkbox'
					},
					{
						id: 'teaching_strategies_g',
						text: 'Culturally responsive teaching practices: Relates content to students’ cultural context.',
						control: 'checkbox'
					},
					{
						id: 'teaching_strategies_h',
						text: 'Civic engagement: Provides opportunities to apply learning through informed action.',
						control: 'checkbox'
					},
				]
			},
		],
	}},

	computed: {
		...mapState(['site_config', 'user_info']),

		user_can_edit() {
			if (this.$parent.user_is_admin) return true

			let roles = this.$parent.publish_roles ?? []

			switch(this.$parent.local_item.publish_status) {
				case 'ready_for_publication':
				case 'published':
					return (roles.includes('publisher')) ? true : false
				case 'under_review':
					return (roles.includes('reviewer')) ? true : false
				default:
					return (roles.includes('author')) ? true : false
			}
		},

		user_can_comment() {
			/** TODO: set comment permissions by role and status */
			return true
		},

		show_review_comments() {return true},

		item() { return this.$parent.item },

		// TODO: currently we get `type` in AssetPublishStatus and `asset_type` in AssetPublishStatus2
		asset_type() { return this.$parent.asset_type || this.$parent.type },

		asset_type_noun() { return this.$parent.asset_type_noun },

		total_score_pct() {
			if (empty(this.lreview)) return 0
			return (this.lreview.total_score / this.lreview.max_total_score) * 100
		},

		total_score_display() {
			// if no review values have been entered, show '–'; otherwise show total_score
			for (let section of this.template) {
				if (!section.asset_types.includes(this.asset_type)) continue

				let ss = this.lreview.sections[section.id].score
				// if we find a non-null section score, return the total_score
				if (ss !== null) return this.lreview.total_score
			}
			// if we get to here, send '–'
			return '–'
		},

		total_score_color() {
			if (empty(this.lreview) || this.total_score_display == '–') return 'k-review-grey'
			if (this.total_score_pct == 100) return 'green'
			if (this.total_score_pct > 75) return 'blue'
			if (this.total_score_pct > 50) return 'purple'
			if (this.total_score_pct > 25) return 'orange'
			return 'red'
		},
	},

	watch: {
		lreview: { deep:true, handler() {
			if (this.saved_review_json != JSON.stringify(this.lreview)) {
				this.save_review()
			}
		}},

		selected_template() {
			if (this.selected_template != 'default') this.$alert('Configurable HQIM rubrics will be available soon. For now please test things out with the provided Default rubric.')
			this.selected_template = 'default'
		},
	},

	mounted() {
		vapp.asset_review_component = this

		// make local copy of review
		this.lreview = new Asset_Review(this.asset_review_data, this.template, this.item)

		// set initial saved_review_json
		this.saved_review_json = JSON.stringify(this.lreview)
			
		let crs = {}
		let cms = {}
		let nc = {}
		for (let section of this.template) {
			// skip sections not relevant for this asset type
			if (!section.asset_types.includes(this.asset_type)) continue

			// set up criteria_showing, comments_showing, and new_comment so they are properly reactive
			crs[section.id] = false
			nc[section.id] = false
			if (section.criteria) for (let c of section.criteria) {
				if (this.lreview.sections[section.id].criteria !== null) {
					// if any criteria are non-null for this section, show criteria for this section by default
					crs[section.id] = true
				}
			}

			cms[section.id] = (this.lreview.sections[section.id].comments.length > 0)
		}
		this.criteria_showing = crs
		this.comments_showing = cms
		this.new_comment = nc
	},

	methods: {
		template_string(s) {
			return s.replace(/\$asset_type\b/g, this.asset_type_noun)
		},

		scale_score_class(section, score) {
			let rv = this.user_can_edit ? '' : 'k-unclickable '

			switch (score) {
				case null:
				// score === null: no special class needed
					break;
				case 0:
				case '0':
				// score == 0: always use this class:
					rv += 'k-asset-review-section-score-0'
					break;
				default:
					rv += 'k-asset-review-section-score-hi'
					let n_scores = 0, index = 0

					for (let scale_score in section.scale) {
						if (scale_score == score) {
							index = n_scores
						}
						++n_scores
					}

					if (index != (n_scores - 1)) {
						rv += '-' + (n_scores - 1 - index)
					}
			}

			return rv
		},

		scale_value_to_show(section) {
			let score = this.lreview.sections[section.id].score

			if (score === null) return '–'
			else return score
		},

		scale_icon(section, scale_value) {
			let score = this.lreview.sections[section.id].score
			return (score == scale_value) ? 'fas fa-circle-dot': 'far fa-circle'
		},

		// called when user clicks to set a value for a section
		scale_value_clicked(section, scale_value) {
			if (!this.user_can_edit) return

			// for standards_alignment section, score can't be > 0 if no standards are aligned
			if (section.id == 'standards_alignment') {
				if (scale_value > 0 && this.item.standards.length == 0) {
					this.$alert(`You cannot set the score for ${section.title} above 0, because the ${this.asset_type_noun} is not aligned to any standards.`)
					return
				}
			}

			this.lreview.sections[section.id].score = scale_value
			this.lreview.compute_total_score()
		},

		generate_new_comment(section_id) {
			this.new_comment[section_id] = new Asset_Review_Comment({
				comment_id: 'new',
				author_email: this.user_info.email,
				author_name: `${this.user_info.first_name} ${this.user_info.last_name}`,
				author_id: this.user_info.user_id,
			})
		},

		async save_comment(section, comment) {
			let c = new Asset_Review_Comment(comment)
			await c.set_created_at()

			if (c.comment_id == 'new') {
				await c.set_new_id()
				this.lreview.sections[section.id].comments.push(c)
			} else {
				let updated = this.lreview.sections[section.id].comments.find(({comment_id}) => comment_id == c.comment_id)
				updated = Object.assign(updated, c)
			}
			
			this.new_comment[section.id] = false
		},

		delete_comment(section, comment) {
			this.lreview.sections[section.id].comments = this.lreview.sections[section.id].comments.filter(({comment_id}) => comment_id != comment.comment_id)
		},

		save_review() {
			// establish the debounce fn if necessary
			if (empty(this.fn_debounced)) {
				this.fn_debounced = U.debounce(function(x) {
					// for now we will send all data for the review in; we could instead only send updated data
					// note that for new reviews, asset_review_id will be 0 when we send, but will be set to a new integer when the service returns
					let updated_review_data = this.lreview.data_for_save()

					let payload = {
						user_id: this.user_info.user_id,
						// encode blocked keywords in the lp_data to avoid firewall issues; setting encode_blocked_keywords tells ajax to decode on the server
						updated_review_data: U.encode_blocked_keywords(JSON.stringify(updated_review_data)),
						encode_blocked_keywords: ['updated_review_data'],
					}
			
					U.ajax('save_asset_review', payload, result => {
						if (result.status != 'ok') {
							this.$alert('An error occurred when attempting to save the review. Has your session timed out?')
							return
						}

						// update the client review and reset saved_review_json
						this.lreview = new Asset_Review(result.review_data, this.template)
						this.saved_review_json = JSON.stringify(this.lreview)

						// TODO: pass to parent
						this.$emit('review_saved', this.lreview)
					})
				}, 100)
			}
			// call the debounce fn
			this.fn_debounced()
		},
	}
}
</script>

<style lang="scss">
.k-asset-review {
	margin:12px auto;
	border-radius:12px;
	padding:12px;
	font-size:14px;
	line-height:18px;
	max-width:740px;
	box-shadow: 0 1px 3px 1px rgba(64, 64, 64, 0.5);
}

.k-asset-review-section-extra {
	margin-top:8px;
	padding-top:8px;
	border-top:1px solid #aaa;
}

.k-asset-review-full-true {
	max-width:none;
}

.k-asset-review-header {
	font-size:16px;
}

.k-asset-review-section {
	border-top:1px solid #444;
	margin-top:12px;
	padding-top:12px;
}

.k-asset-review-section-title {
	font-size:16px;
}

.k-asset-review-section-score {
	width:32px;
	height:22px;
	line-height:22px;
	border-radius:4px;
	font-weight:bold;
	text-align:center;
	// this is the background-color and color when the score isn't set
	background-color: #ccc;
	color:#000;
}

.k-asset-review-section-score.k-asset-review-section-score-0 { background-color:$v-red-darken-4; color:#fff; }
.k-asset-review-section-score.k-asset-review-section-score-hi-3 { background-color:$v-orange-darken-4; color:#fff; }
.k-asset-review-section-score.k-asset-review-section-score-hi-2 { background-color:$v-purple-darken-4; color:#fff; }
.k-asset-review-section-score.k-asset-review-section-score-hi-1 { background-color:$v-blue-darken-4; color:#fff; }
.k-asset-review-section-score.k-asset-review-section-score-hi { background-color:$v-green-darken-4; color:#fff; }

.k-asset-review-section-scale-header {
	.v-icon.k-asset-review-section-score-0 { color:$v-red-darken-4; }
	.v-icon.k-asset-review-section-score-hi-3 { color:$v-orange-darken-4; }
	.v-icon.k-asset-review-section-score-hi-2 { color:$v-purple-darken-4; }
	.v-icon.k-asset-review-section-score-hi-1 { color:$v-blue-darken-4; }
	.v-icon.k-asset-review-section-score-hi { color:$v-green-darken-4; }

	.k-asset-review-section-scale-header-value.k-asset-review-section-score-0 { color:$v-red-darken-4; }
	.k-asset-review-section-scale-header-value.k-asset-review-section-score-hi-3 { color:$v-orange-darken-4; }
	.k-asset-review-section-scale-header-value.k-asset-review-section-score-hi-2 { color:$v-purple-darken-4; }
	.k-asset-review-section-scale-header-value.k-asset-review-section-score-hi-1 { color:$v-blue-darken-4; }
	.k-asset-review-section-scale-header-value.k-asset-review-section-score-hi { color:$v-green-darken-4; }
}

.k-unclickable {
	cursor: default !important;
}

.k-review-grey {
	background-color:#fff;
}

.v-progress-linear__background.k-review-grey {
	background-color:#999!important;
}
</style>